import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  DayCalendar,
  IDayCalendar,
  StyledBoard,
  StyledCalendar,
  StyledMonthYear,
  StyledNextMonth,
  StyledNextYear,
  StyledPreviosMonth,
  StyledPreviosYear,
  StyledSpan,
  StyledToday
} from "./styles";
import { FlexBox } from "../containers";
import { MONTHS, WEEK } from "../../utils/constants";
import { DateNone } from "interfaces/intarfaces";
import { isoDate } from "utils/helpers";

export interface ICalendarIn {
  value?: DateNone;
  endInterval?: DateNone;
  range?: boolean;
  firstDayOfWeek?: number; // 1 - Sunday, 2 - Monday
}

export interface ICalendar extends ICalendarIn {
  onChange: (a: DateNone, b: DateNone) => void;
  setIsAnyDateOut?: (isAnyDate: boolean) => void;
  leftBorder?: any;
  rightBorder?: any;
  error?: string;
  anyEndDate?: boolean;
}

export const useCalendar = ({
  value = undefined,
  endInterval = undefined,
  range = false
}: ICalendarIn): ICalendar => {
  const [begin, setBegin] = useState<DateNone>(value);
  const [end, setEnd] = useState<DateNone>(endInterval);

  return {
    value: begin,
    endInterval: end,
    onChange: (a: DateNone, b: DateNone) => {
      setBegin(a);
      setEnd(b);
    },
    range
  };
};

const Calendar: React.FC<ICalendar> = ({
  value = undefined,
  endInterval = undefined,
  onChange,
  range = false,
  firstDayOfWeek = 1,
  setIsAnyDateOut,
  leftBorder,
  rightBorder,
  anyEndDate,
  error
}) => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState<Date>(value ?? new Date());

  const [beginInterval, setBeginInterval] = useState(value);
  const [finishInterval, setFinishInterval] = useState(endInterval);
  const [selectedBegin, setSelectedBegin] = useState(true);

  React.useEffect(() => {
    if (finishInterval !== undefined) {
      onChange(beginInterval, finishInterval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishInterval]);

  React.useEffect(() => {
    // If 'noenddate' is selected, somehow it is sometimes 2101-01-01 sometimes Dec 31 2100 12:00:01, therefor just bigger thatn 2050?
    if (endInterval && endInterval.toISOString() > "2050-01-01T00:00:00.000Z") {
      setCurrentDate(new Date()); // Set to today's date
    } else if (endInterval) {
      setCurrentDate(endInterval); // Set to the previously selected date
    }
  }, [endInterval]);

  const changeBoundary = (date: Date) => {
    setIsAnyDateOut && setIsAnyDateOut(false);
    if (selectedBegin) {
      setBeginInterval(date);
      setFinishInterval(undefined);
      if (!range) {
        onChange(date, date);
        return;
      }
      onChange(undefined, undefined);
    } else {
      if (beginInterval && date < beginInterval) {
        setFinishInterval(beginInterval);
        setBeginInterval(date);
      } else setFinishInterval(date);
    }
    setSelectedBegin(!selectedBegin);
  };

  const changeMonth = (direction: boolean) => {
    setCurrentDate(
      new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + (direction ? 1 : -1),
        1,
        12,
        0,
        1,
        0
      )
    );
  };

  const changeYear = (x: number) => {
    const newDate: Date = new Date(
      currentDate.setFullYear(currentDate.getFullYear() + x)
    );
    setCurrentDate(newDate);
  };

  const setToday = () => {
    setCurrentDate(new Date());
    changeBoundary(new Date());
  };
  return (
    <StyledCalendar style={{ border: error ? "1px solid red" : "" }}>
      <FlexBox justify="space-between" margin="1em 0">
        <StyledMonthYear>
          {`${t(
            `calendar.months.${MONTHS[currentDate.getMonth()]}`
          )} ${currentDate.getFullYear()}`}
        </StyledMonthYear>
        <FlexBox justify="flex-end">
          {/* <StyledPreviosDecade size="1em" onClick={() => changeYear(-10)} /> */}
          <StyledPreviosYear size="1em" onClick={() => changeYear(-1)} />
          <StyledPreviosMonth size="1.3em" onClick={() => changeMonth(false)} />
          {/* <StyledToday size="0.7em" onClick={() => setToday()} /> */}
          <StyledNextMonth size="1.3em" onClick={() => changeMonth(true)} />
          <StyledNextYear size="1em" onClick={() => changeYear(1)} />
          {/* <StyledNextDecade size="1em" onClick={() => changeYear(10)} /> */}
        </FlexBox>
      </FlexBox>
      <StyledBoard>
        {Array.from(
          firstDayOfWeek === 1 ? Array(7).keys() : [1, 2, 3, 4, 5, 6, 0]
        ).map((i) => (
          <StyledSpan key={i}>{t(`calendar.week.${WEEK[i]}`)}</StyledSpan>
        ))}
        <DayBoard
          anyEndDate={anyEndDate}
          leftBorder={leftBorder}
          rightBorder={rightBorder}
          currentMonth={currentDate.toISOString().slice(0, 7)}
          firstWeekDay={firstDayOfWeek}
          changeBoundary={changeBoundary}
          begin={isoDate(beginInterval)}
          end={isoDate(finishInterval)}
        />
      </StyledBoard>
    </StyledCalendar>
  );
};

const boardMonth = (currentMonth: string, firstDayOfWeek: number): Date[] => {
  const currentDate = new Date(currentMonth + "-01");
  const y = currentDate.getFullYear();
  const m = currentDate.getMonth();
  let firstDay = currentDate.getDay();
  let listD = [];
  const sizeBoard = firstDay + new Date(y, m + 1, 0).getDate() > 35 ? 42 : 35;
  for (let cell in Array.from({ length: sizeBoard }, (_, i) => i)) {
    listD.push(
      new Date(y, m, Number(cell) - firstDay + firstDayOfWeek, 12, 0, 1, 0)
    );
  }
  return listD;
};

interface IBoard {
  currentMonth: string;
  firstWeekDay?: number;
  rightBorder?: any;
  leftBorder?: any;
  changeBoundary: (d: Date) => void;
  begin: string;
  end: string;
  anyEndDate?: boolean | undefined;
}

const DayBoard: React.FC<IBoard> = React.memo(
  ({
    currentMonth = new Date().toISOString().slice(0, 7),
    firstWeekDay = 1,
    changeBoundary,
    begin = "",
    end = "",
    rightBorder,
    leftBorder,
    anyEndDate
  }) => {
    const days = React.useMemo(
      () => boardMonth(currentMonth, firstWeekDay),
      [currentMonth, firstWeekDay]
    );

    const stateDate = (date: Date): IDayCalendar => {
      const dt = isoDate(date);
      if (dt === begin || dt === end) return { boundaryInterval: true };

      let temp = {};
      if (dt.slice(0, 7) !== currentMonth) temp = { strangerDay: true };
      if (begin.length > 0 && end.length > 0 && dt > begin && dt < end)
        return { insideInterval: true, ...temp };
      return { ...temp };
    };

    const [minDate, setMinDate] = useState<any>();

    useEffect(() => {
      leftBorder && setMinDate(new Date(new Date(leftBorder).getTime()));
    }, [leftBorder, rightBorder]);

    return (
      <>
        {days.map((day, j) => {
          return (
            <DayCalendar
              style={
                minDate
                  ? day <= minDate
                    ? {
                      pointerEvents: "none",
                      background: `rgba(208,208,208,0.3)`
                    }
                    : undefined
                  : rightBorder && !anyEndDate
                    ? day >= rightBorder
                      ? {
                        pointerEvents: "none",
                        background: `rgba(208,208,208,0.3)`
                      }
                      : undefined
                    : undefined
              }
              {...stateDate(day)}
              today={
                day.getDate() === new Date().getDate() &&
                day.getMonth() === new Date().getMonth()
              }
              onClick={() => changeBoundary(day)}
              key={j}
            >
              {day.getDate()}
            </DayCalendar>
          );
        })}
      </>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.currentMonth !== nextProps.currentMonth) return false;
    if (prevProps.firstWeekDay !== nextProps.firstWeekDay) return false;
    if (prevProps.begin !== nextProps.begin) return false;
    if (prevProps.end !== nextProps.end) return false;
    return true;
  }
);

export default React.memo(Calendar, (prevProps, nextProps) => {
  // if (prevProps.value !== nextProps.value) return false;
  // if (prevProps.endInterval !== nextProps.endInterval) return false;
  return false;
});
